/**
 * Shared surface primitives for demo dashboards and panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import {
  DISPLAY_FONT,
  HEADING_FONT,
  MATH_MONO_FONT,
  MONO_FONT,
  OVERRIDE,
  OVERRIDE_SOFT,
} from "./styles.js";

export type DemoSurfaceTone = "light" | "dark" | "inherit";

function joinClassNames(...classes: readonly (string | undefined)[]): string | undefined {
  const joined = classes
    .filter((value): value is string => value !== undefined && value.length > 0)
    .join(" ");
  return joined.length > 0 ? joined : undefined;
}

function surfaceStyle(tone: DemoSurfaceTone): React.CSSProperties {
  if (tone === "inherit") return {};
  if (tone === "dark") {
    return {
      background: "rgba(15, 23, 42, 0.78)",
      border: "1px solid rgba(71, 85, 105, 0.58)",
      boxShadow: "0 10px 28px rgba(2, 6, 23, 0.25)",
      color: "#cbd5e1",
    };
  }
  return {
    background: "white",
    border: `1px solid ${GRAY[300]}`,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    color: GRAY[700],
  };
}

function headingColor(tone: DemoSurfaceTone): string {
  return tone === "light" ? GRAY[900] : "#e2e8f0";
}

function mutedColor(tone: DemoSurfaceTone): string {
  return tone === "light" ? GRAY[600] : "#94a3b8";
}

export interface DemoSurfaceCardProps {
  readonly title?: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly compact?: boolean;
  readonly tone?: DemoSurfaceTone;
  readonly className?: string;
  readonly bodyClassName?: string;
  readonly role?: React.AriaRole;
  readonly style?: React.CSSProperties;
  readonly ariaLabel?: string;
}

export function DemoSurfaceCard({
  title,
  description,
  actions,
  icon,
  children,
  footer,
  compact = false,
  tone = "light",
  className,
  bodyClassName,
  role,
  style,
  ariaLabel,
}: DemoSurfaceCardProps): React.JSX.Element {
  const hasHeader =
    title !== undefined || description !== undefined || actions !== undefined || icon !== undefined;
  const label = ariaLabel ?? (typeof title === "string" ? title : undefined);
  return (
    <section
      aria-label={label}
      className={className}
      role={role}
      style={{
        borderRadius: 8,
        padding: compact ? 12 : 16,
        ...surfaceStyle(tone),
        ...style,
      }}
    >
      {hasHeader ? (
        <header
          style={{
            alignItems: "flex-start",
            display: "flex",
            gap: 12,
            justifyContent: "space-between",
          }}
        >
          <div style={{ alignItems: "center", display: "flex", gap: 10, minWidth: 0 }}>
            {icon !== undefined ? <span style={{ flexShrink: 0 }}>{icon}</span> : null}
            <div style={{ minWidth: 0 }}>
              {title !== undefined ? (
                <h3
                  style={{
                    color: headingColor(tone),
                    fontFamily: HEADING_FONT,
                    fontSize: compact ? 14 : 18,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    margin: 0,
                  }}
                >
                  {title}
                </h3>
              ) : null}
              {description !== undefined ? (
                <p
                  style={{
                    color: mutedColor(tone),
                    fontSize: 13,
                    lineHeight: 1.55,
                    margin: "4px 0 0",
                  }}
                >
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          {actions !== undefined ? (
            <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8 }}>
              {actions}
            </div>
          ) : null}
        </header>
      ) : null}
      {children !== undefined ? (
        <div className={bodyClassName} style={{ marginTop: hasHeader ? 12 : 0 }}>
          {children}
        </div>
      ) : null}
      {footer !== undefined ? (
        <footer
          style={{
            borderTop:
              tone === "light" ? `1px solid ${GRAY[300]}` : "1px solid rgba(71, 85, 105, 0.5)",
            color: mutedColor(tone),
            fontSize: 12,
            marginTop: 14,
            paddingTop: 12,
          }}
        >
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

export type DemoPillVariant =
  | "default"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "seam"
  | "override";

type PillPalette = Readonly<
  Record<DemoPillVariant, { readonly bg: string; readonly border: string; readonly fg: string }>
>;

const LIGHT_PILL_COLOR: PillPalette = {
  default: { bg: "rgba(148, 163, 184, 0.14)", border: "rgba(148, 163, 184, 0.34)", fg: GRAY[700] },
  success: { bg: SAFE[100], border: "rgba(16, 185, 129, 0.28)", fg: SAFE[700] },
  warning: { bg: "#fef3c7", border: "rgba(245, 158, 11, 0.3)", fg: "#92400e" },
  danger: { bg: WARN[100], border: "rgba(239, 68, 68, 0.32)", fg: WARN[700] },
  info: { bg: "rgba(20, 184, 166, 0.12)", border: "rgba(20, 184, 166, 0.28)", fg: "#0f766e" },
  seam: { bg: PRIMARY[100], border: "rgba(14, 165, 233, 0.28)", fg: PRIMARY[700] },
  override: { bg: OVERRIDE_SOFT, border: "rgba(124, 58, 237, 0.3)", fg: OVERRIDE },
};

const DARK_PILL_COLOR: PillPalette = {
  default: { bg: "rgba(30, 41, 59, 0.72)", border: "rgba(100, 116, 139, 0.42)", fg: "#cbd5e1" },
  success: { bg: "rgba(16, 185, 129, 0.12)", border: "rgba(16, 185, 129, 0.28)", fg: "#34d399" },
  warning: { bg: "rgba(245, 158, 11, 0.12)", border: "rgba(245, 158, 11, 0.32)", fg: "#fbbf24" },
  danger: { bg: "rgba(239, 68, 68, 0.12)", border: "rgba(239, 68, 68, 0.32)", fg: "#f87171" },
  info: { bg: "rgba(20, 184, 166, 0.12)", border: "rgba(20, 184, 166, 0.28)", fg: "#2dd4bf" },
  seam: { bg: "rgba(14, 165, 233, 0.12)", border: "rgba(14, 165, 233, 0.28)", fg: "#38bdf8" },
  override: { bg: "rgba(124, 58, 237, 0.14)", border: "rgba(124, 58, 237, 0.3)", fg: "#a78bfa" },
};

export interface DemoPillProps {
  readonly children: React.ReactNode;
  readonly variant?: DemoPillVariant;
  readonly tone?: DemoSurfaceTone;
  readonly mono?: boolean | "math";
  readonly ariaLabel?: string;
  readonly ariaPressed?: boolean;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly title?: string;
}

export function DemoPill({
  children,
  variant = "default",
  tone = "light",
  mono = false,
  ariaLabel,
  ariaPressed,
  className,
  disabled = false,
  onClick,
  title,
}: DemoPillProps): React.JSX.Element {
  const palette = (tone === "light" ? LIGHT_PILL_COLOR : DARK_PILL_COLOR)[variant];
  const monoFont = mono === "math" ? MATH_MONO_FONT : mono ? MONO_FONT : undefined;
  const interactive = onClick !== undefined;
  const sharedStyle: React.CSSProperties = {
    alignItems: "center",
    background: palette.bg,
    border: `1px solid ${palette.border}`,
    borderRadius: 999,
    color: palette.fg,
    cursor: interactive ? (disabled ? "not-allowed" : "pointer") : undefined,
    display: "inline-flex",
    fontFamily: monoFont,
    fontSize: 11,
    fontVariantNumeric: monoFont !== undefined ? "tabular-nums" : undefined,
    fontWeight: 700,
    lineHeight: 1.1,
    opacity: disabled ? 0.55 : 1,
    padding: "3px 8px",
    whiteSpace: "nowrap",
  };
  if (interactive) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        disabled={disabled}
        onClick={onClick}
        title={title}
        className={joinClassNames("demo-pill", className)}
        style={sharedStyle}
      >
        {children}
      </button>
    );
  }
  return (
    <span
      aria-label={ariaLabel}
      title={title}
      className={joinClassNames("demo-pill", className)}
      style={sharedStyle}
    >
      {children}
    </span>
  );
}

export interface DemoMetricCardProps {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly sub?: React.ReactNode;
  readonly color?: string;
  readonly tone?: DemoSurfaceTone;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function DemoMetricCard({
  label,
  value,
  sub,
  color = PRIMARY[700],
  tone = "light",
  className,
  style,
}: DemoMetricCardProps): React.JSX.Element {
  return (
    <div
      className={className}
      style={{
        borderRadius: 8,
        padding: 12,
        textAlign: "center",
        ...surfaceStyle(tone),
        ...style,
      }}
    >
      <div
        style={{
          color: mutedColor(tone),
          fontFamily: HEADING_FONT,
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          color,
          fontFamily: DISPLAY_FONT,
          fontFeatureSettings: '"tnum" 1',
          fontSize: 24,
          fontVariationSettings: '"SOFT" 65, "WONK" 1',
          fontWeight: 800,
          marginTop: 4,
        }}
      >
        {value}
      </div>
      {sub !== undefined ? (
        <div style={{ color: mutedColor(tone), fontSize: 11, marginTop: 4 }}>{sub}</div>
      ) : null}
    </div>
  );
}

export interface DemoResponsiveGridProps {
  readonly children: React.ReactNode;
  readonly minColumnWidth?: number;
  readonly gap?: number | string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function DemoResponsiveGrid({
  children,
  minColumnWidth = 220,
  gap = 16,
  className,
  style,
}: DemoResponsiveGridProps): React.JSX.Element {
  return (
    <div
      className={className}
      style={{
        display: "grid",
        gap,
        gridTemplateColumns: `repeat(auto-fit, minmax(${String(minColumnWidth)}px, 1fr))`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface DemoDataTableColumn<Row> {
  readonly id: string;
  readonly header: React.ReactNode;
  readonly cell: (row: Row, rowIndex: number) => React.ReactNode;
  readonly align?: "left" | "center" | "right";
  readonly width?: number | string;
}

export interface DemoDataTableProps<Row> {
  readonly columns: readonly DemoDataTableColumn<Row>[];
  readonly rows: readonly Row[];
  readonly getRowKey: (row: Row, rowIndex: number) => React.Key;
  readonly compact?: boolean;
  readonly tone?: DemoSurfaceTone;
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly minWidth?: number;
  readonly emptyState?: React.ReactNode;
}

export function DemoDataTable<Row>({
  columns,
  rows,
  getRowKey,
  compact = false,
  tone = "light",
  className,
  ariaLabel,
  minWidth = 680,
  emptyState = "No rows",
}: DemoDataTableProps<Row>): React.JSX.Element {
  const pad = compact ? "6px 8px" : "10px 8px";
  const borderColor = tone === "light" ? GRAY[300] : "rgba(71, 85, 105, 0.58)";
  return (
    <div className={className} style={{ overflowX: "auto" }}>
      <table aria-label={ariaLabel} style={{ borderCollapse: "collapse", minWidth, width: "100%" }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                style={{
                  borderBottom: `1px solid ${borderColor}`,
                  color: mutedColor(tone),
                  fontSize: 12,
                  fontWeight: 700,
                  padding: pad,
                  textAlign: column.align ?? "left",
                  textTransform: "uppercase",
                  width: column.width,
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => (
              <tr key={getRowKey(row, rowIndex)}>
                {columns.map((column) => (
                  <td
                    key={column.id}
                    style={{
                      borderBottom: `1px solid ${tone === "light" ? GRAY[100] : "rgba(30, 41, 59, 0.72)"}`,
                      color: tone === "light" ? GRAY[700] : "#cbd5e1",
                      fontSize: 13,
                      lineHeight: 1.45,
                      padding: pad,
                      textAlign: column.align ?? "left",
                      verticalAlign: "top",
                    }}
                  >
                    {column.cell(row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  color: mutedColor(tone),
                  fontSize: 13,
                  padding: pad,
                  textAlign: "center",
                }}
              >
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
